import * as React from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
  type DashboardDataTableColumn,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import {
  loadedSelectionHeaderState,
  manageDialogFetchSize,
  mergeLoadedSelection,
  pageClientRows,
} from '../../../../../lib/clientSelectionList';
import { useSupplyAppsQuery, useSupplyDomainsQuery } from '../../hooks';
import {
  formatSupplyDate,
  resolveSupplyStatusLabel,
  resolveSupplyStatusTone,
  supplyPageSizes,
  type SupplyApp,
  type SupplyDomain,
} from '../../model';
import { truncatedSelectionValue } from './SelectionDialog.helpers';

const defaultPageSize = 10;

export interface SelectionDialogProps<
  TItem extends { id: string },
  TSelection extends { id: string } = TItem,
> {
  open: boolean;
  title: string;
  description: string;
  items: TItem[];
  selectedItems: TSelection[];
  columns: (options: SelectionColumnOptions<TItem>) => DashboardDataTableColumn<TItem>[];
  minWidthClassName: string;
  loading: boolean;
  errorMessage?: string | undefined;
  getId: (item: TItem) => string;
  searchText: (item: TItem) => string;
  initialPageSize?: number | undefined;
  matches?: ((item: TItem) => boolean) | undefined;
  clientFilterKey?: string | undefined;
  pageSizes?: readonly number[] | undefined;
  filterToolbar?: React.ReactNode | undefined;
  toolbarActions?: React.ReactNode | undefined;
  toSelection?: ((item: TItem) => TSelection) | undefined;
  onOpenChange: (open: boolean) => void;
  onConfirm: (items: TSelection[]) => void;
}

export interface SelectionColumnOptions<TItem> {
  selectedIds: Set<string>;
  onToggleSelection: (item: TItem, checked: boolean) => void;
  headerChecked: boolean | 'indeterminate';
  headerDisabled: boolean;
  headerLabel: string;
  onHeaderChange: (checked: boolean) => void;
}

export function SelectionDialog<
  TItem extends { id: string },
  TSelection extends { id: string } = TItem,
>({
  open,
  title,
  description,
  items,
  selectedItems,
  columns,
  minWidthClassName,
  loading,
  errorMessage,
  getId,
  searchText,
  initialPageSize,
  matches,
  clientFilterKey,
  pageSizes,
  filterToolbar,
  toolbarActions,
  toSelection,
  onOpenChange,
  onConfirm,
}: SelectionDialogProps<TItem, TSelection>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? (
        <SelectionDialogContent
          key={selectedItems.map((item) => item.id).join(':')}
          title={title}
          description={description}
          items={items}
          selectedItems={selectedItems}
          columns={columns}
          minWidthClassName={minWidthClassName}
          loading={loading}
          errorMessage={errorMessage}
          getId={getId}
          searchText={searchText}
          initialPageSize={initialPageSize}
          matches={matches}
          clientFilterKey={clientFilterKey}
          pageSizes={pageSizes}
          filterToolbar={filterToolbar}
          toolbarActions={toolbarActions}
          toSelection={toSelection}
          onCancel={() => {
            onOpenChange(false);
          }}
          onConfirm={(next) => {
            onConfirm(next);
            onOpenChange(false);
          }}
        />
      ) : null}
    </Dialog>
  );
}

function SelectionDialogContent<TItem extends { id: string }, TSelection extends { id: string }>({
  title,
  description,
  items,
  selectedItems,
  columns: buildColumns,
  minWidthClassName,
  loading,
  errorMessage,
  getId,
  searchText,
  initialPageSize = defaultPageSize,
  matches,
  clientFilterKey,
  pageSizes = supplyPageSizes,
  filterToolbar,
  toolbarActions,
  toSelection,
  onCancel,
  onConfirm,
}: Omit<SelectionDialogProps<TItem, TSelection>, 'open' | 'onOpenChange'> & {
  onCancel: () => void;
}) {
  const [draft, setDraft] = React.useState(selectedItems);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [appliedFilterKey, setAppliedFilterKey] = React.useState(clientFilterKey);
  const selectedIds = React.useMemo(() => new Set(draft.map((item) => item.id)), [draft]);
  if (clientFilterKey !== appliedFilterKey) {
    setAppliedFilterKey(clientFilterKey);
    setPage(0);
  }
  const paged = pageClientRows({
    items,
    searchTerm,
    searchText,
    page,
    pageSize,
    matches,
  });
  const headerState = loadedSelectionHeaderState(selectedIds, paged.filteredItems.map(getId));
  const toDraft = React.useCallback(
    (item: TItem) => (toSelection ? toSelection(item) : (item as unknown as TSelection)),
    [toSelection],
  );
  const onToggleSelection = React.useCallback(
    (item: TItem, checked: boolean) => {
      setDraft((current) =>
        checked
          ? [...current.filter((candidate) => candidate.id !== getId(item)), toDraft(item)]
          : current.filter((candidate) => candidate.id !== getId(item)),
      );
    },
    [getId, toDraft],
  );
  const onHeaderChange = React.useCallback(
    (checked: boolean) => {
      setDraft((current) =>
        mergeLoadedSelection(
          current,
          paged.filteredItems,
          checked,
          (item) => item.id,
          getId,
          toDraft,
        ),
      );
    },
    [getId, paged.filteredItems, toDraft],
  );
  const columns = React.useMemo(
    () =>
      buildColumns({
        selectedIds,
        onToggleSelection,
        headerChecked: headerState.checked,
        headerDisabled: headerState.disabled,
        headerLabel: dashboardT('common.selectAll'),
        onHeaderChange,
      }),
    [
      buildColumns,
      headerState.checked,
      headerState.disabled,
      onHeaderChange,
      onToggleSelection,
      selectedIds,
    ],
  );
  return (
    <DashboardTableDialogContent>
      <DashboardTableDialogHeader title={title} description={description} />
      <DashboardTableDialogBody>
        <DashboardTableDialogDataTable
          tableLabel={title}
          columns={columns}
          items={paged.items}
          getRowKey={getId}
          loading={loading}
          loadingMessage={dashboardT('supply.loading')}
          errorMessage={errorMessage}
          emptyMessage={dashboardT('supply.emptySelection')}
          minWidthClassName={minWidthClassName}
          toolbar={
            <>
              <DashboardTableSearchInput
                aria-label={dashboardT('supply.search')}
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(0);
                }}
              />
              {toolbarActions}
            </>
          }
          filterToolbar={filterToolbar}
          pagination={{
            value: { page: paged.page, pageSize, totalItems: paged.totalItems },
            pageSizes,
            disabled: loading,
            onChange: (next) => {
              setPage(next.page);
              setPageSize(next.pageSize);
            },
          }}
        />
      </DashboardTableDialogBody>
      <DashboardTableDialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {dashboardT('supply.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => {
            onConfirm(draft);
          }}
        >
          {dashboardT('supply.select')}
        </Button>
      </DashboardTableDialogFooter>
    </DashboardTableDialogContent>
  );
}

function supplyLookupFilter(companyIds?: readonly string[]) {
  return {
    searchTerm: '',
    page: 0,
    size: manageDialogFetchSize,
    ...(companyIds ? { companyIds } : {}),
  };
}

export function ManageAppsDialog({
  companyIds,
  open,
  selectedItems,
  onOpenChange,
  onConfirm,
}: {
  companyIds?: readonly string[] | undefined;
  open: boolean;
  selectedItems: SupplyApp[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (items: SupplyApp[]) => void;
}) {
  const query = useSupplyAppsQuery(supplyLookupFilter(companyIds), open);
  return (
    <SelectionDialog
      open={open}
      title={dashboardT('supply.apps')}
      description={dashboardT('supply.manageAppsDescription')}
      items={query.data?.content ?? []}
      selectedItems={selectedItems}
      columns={buildAppsSelectionColumns}
      minWidthClassName="min-w-[880px]"
      loading={query.isLoading}
      errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
      getId={(item) => item.id}
      searchText={(item) => item.name}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
    />
  );
}

export function ManageDomainsDialog({
  companyIds,
  open,
  selectedItems,
  onOpenChange,
  onConfirm,
}: {
  companyIds?: readonly string[] | undefined;
  open: boolean;
  selectedItems: SupplyDomain[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (items: SupplyDomain[]) => void;
}) {
  const query = useSupplyDomainsQuery(supplyLookupFilter(companyIds), open);
  return (
    <SelectionDialog
      open={open}
      title={dashboardT('supply.domains')}
      description={dashboardT('supply.manageDomainsDescription')}
      items={query.data?.content ?? []}
      selectedItems={selectedItems}
      columns={buildDomainsSelectionColumns}
      minWidthClassName="min-w-[640px]"
      loading={query.isLoading}
      errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
      getId={(item) => item.id}
      searchText={(item) => item.url}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
    />
  );
}

function buildAppsSelectionColumns({
  selectedIds,
  onToggleSelection,
  headerChecked,
  headerDisabled,
  headerLabel,
  onHeaderChange,
}: SelectionColumnOptions<SupplyApp>) {
  return buildDashboardDataTableColumns<SupplyApp>([
    {
      kind: DashboardDataTableColumnKind.Selection,
      id: 'select',
      header: '',
      checked: (app) => selectedIds.has(app.id),
      label: (app) => dashboardT('supply.selectNamed', { name: app.name }),
      onChange: onToggleSelection,
      headerChecked,
      headerDisabled,
      headerLabel,
      onHeaderChange,
    },
    {
      kind: DashboardDataTableColumnKind.Custom,
      id: 'name',
      header: dashboardT('supply.name'),
      headerClassName: 'w-56 pl-4',
      className: 'w-56 max-w-56 pl-4 pr-2 font-medium',
      cell: (app) => truncatedSelectionValue(app.name),
    },
    {
      kind: DashboardDataTableColumnKind.Status,
      id: 'status',
      header: dashboardT('supply.status'),
      tone: (app) => resolveSupplyStatusTone(app.status),
      label: (app) => resolveSupplyStatusLabel(app.status),
    },
    {
      kind: DashboardDataTableColumnKind.Text,
      id: 'appStoreId',
      header: dashboardT('supply.appStoreId'),
      value: (app) => app.appStoreId,
    },
    {
      kind: DashboardDataTableColumnKind.Text,
      id: 'platform',
      header: dashboardT('supply.platform'),
      value: (app) => app.platform?.name,
    },
    {
      kind: DashboardDataTableColumnKind.Custom,
      id: 'bundleId',
      header: dashboardT('supply.bundleId'),
      headerClassName: 'w-56',
      className: 'w-56 max-w-56',
      cell: (app) => truncatedSelectionValue(app.bundleId),
    },
    {
      kind: DashboardDataTableColumnKind.Date,
      id: 'created',
      header: dashboardT('supply.created'),
      headerClassName: 'w-28',
      className: 'w-28 whitespace-nowrap',
      value: (app) => formatSupplyDate(app.createdOn),
    },
  ]);
}

function buildDomainsSelectionColumns({
  selectedIds,
  onToggleSelection,
  headerChecked,
  headerDisabled,
  headerLabel,
  onHeaderChange,
}: SelectionColumnOptions<SupplyDomain>) {
  return buildDashboardDataTableColumns<SupplyDomain>([
    {
      kind: DashboardDataTableColumnKind.Selection,
      id: 'select',
      header: '',
      checked: (domain) => selectedIds.has(domain.id),
      label: (domain) => dashboardT('supply.selectNamed', { name: domain.url }),
      onChange: onToggleSelection,
      headerChecked,
      headerDisabled,
      headerLabel,
      onHeaderChange,
    },
    {
      kind: DashboardDataTableColumnKind.Text,
      id: 'url',
      header: dashboardT('supply.url'),
      headerClassName: 'pl-4',
      className: 'pl-4 pr-2 font-medium',
      value: (domain) => domain.url,
    },
    {
      kind: DashboardDataTableColumnKind.Status,
      id: 'status',
      header: dashboardT('supply.status'),
      tone: (domain) => resolveSupplyStatusTone(domain.status),
      label: (domain) => resolveSupplyStatusLabel(domain.status),
    },
    {
      kind: DashboardDataTableColumnKind.Date,
      id: 'created',
      header: dashboardT('supply.created'),
      headerClassName: 'w-28',
      className: 'w-28 whitespace-nowrap',
      value: (domain) => formatSupplyDate(domain.createdOn),
    },
  ]);
}
